import React, { useState, useMemo, useCallback } from 'react'
import { Box, SimpleGrid, useToast, Select, Text, Flex, Spinner, Center } from '@chakra-ui/react'
import { useQuery } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { ArtImage } from '../../../components/ArtImage'
import { ActionButton } from '../../../components/ActionButton'
import { usePatternContext } from '../../../context/PatternContext'
import api, { fetchProducts, getAspectRatio } from '../../../services/api'

async function hasTaskIdCompleted(taskId, updateStatusCallback) {
  try {
    const statusReponse = await api.get(`/site/v2/controle-imagens-ia/status/${taskId}`)
    const statusData = statusReponse.data

    if (updateStatusCallback) {
      updateStatusCallback(statusData.status)
    }

    if (statusData.status === 'completed') {
      const imagensResponse = await api.get(`/site/v2/controle-imagens-ia/imagens/${taskId}`)
      const imagensData = imagensResponse.data

      if (imagensData && imagensData.imagens && Array.isArray(imagensData.imagens)) {
        const imageUrls = imagensData.imagens.slice(1)

        const formattedImages = imageUrls.map((url, index) => ({
          id: index + 1,
          url: url,
          prompt: imagensData.prompt || '',
          dataSolicitacao: imagensData.data_solicitacao || '',
        }))

        return formattedImages
      }

      return imagensData
    }

    return null
  } catch (error) {
    console.error('Erro ao verificar status da tarefa:', error)
    throw error
  }
}

const mockImages = [
  {
    id: 1,
    url: 'https://via.placeholder.com/400x400/FFEB3B/000000?text=Arte+1',
    backgroundColor: '#FFEB3B',
  },
  {
    id: 2,
    url: 'https://via.placeholder.com/400x400/4F6272/FFFFFF?text=Arte+2',
    backgroundColor: '#4F6272',
  },
  {
    id: 3,
    url: 'https://via.placeholder.com/400x400/FFB74D/000000?text=Arte+3',
    backgroundColor: '#FFB74D',
  },
  {
    id: 4,
    url: 'https://via.placeholder.com/400x400/303F9F/FFFFFF?text=Arte+4',
    backgroundColor: '#303F9F',
  },
]

export function ImageSelectionStep() {
  const toast = useToast()
  const [selectedImages, setSelectedImages] = useState({})
  const [selectedProduct, setSelectedProduct] = useState('')
  const { formData, setFormData } = usePatternContext()
  const [isGenerating, setIsGenerating] = useState(false)
  const [taskStatus, setTaskStatus] = useState('waiting')
  const { taskId } = useParams()
  const navigate = useNavigate()

  const { data: products = [], isLoading: isLoadingProducts } = useQuery('products', fetchProducts)

  const fetchTaskStatus = async () => {
    if (!taskId) return null

    try {
      setTaskStatus('checking')
      const images = await hasTaskIdCompleted(taskId, setTaskStatus)
      if (images && images.length > 0) {
        setTaskStatus('completed')
        return images
      }
      return null
    } catch (error) {
      console.error('Erro ao verificar status da tarefa:', error)
      toast({
        title: 'Erro ao carregar imagens',
        description: 'Não foi possível verificar o status da tarefa.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return null
    }
  }

  const { data: taskImages, isLoading: isLoadingTaskImages } = useQuery(['taskImages', taskId], fetchTaskStatus, {
    enabled: !!taskId, // Só executa se tiver um taskId
    refetchInterval: taskStatus !== 'completed' ? 5000 : false, // Refetch a cada 5 segundos se não estiver completo
    refetchOnWindowFocus: true,
    staleTime: 0,
    // Garante que a consulta seja executada imediatamente quando taskId mudar
    refetchOnMount: true,
    retry: 3,
  })

  const isLoadingArts = taskStatus !== 'completed'

  // Determinar as artes a serem exibidas
  const generatedArts = useMemo(() => {
    if (formData.generatedImages && formData.generatedImages.length > 0) {
      return formData.generatedImages
    }

    if (taskImages && taskImages.length > 0) {
      // Adicionar metadados adicionais se necessário
      return taskImages.map((img) => ({
        ...img,
        backgroundColor: img.backgroundColor || '#ffffff', // Cor de fundo padrão se não existir
      }))
    }

    // Apenas retorne imagens mock se não estiver carregando
    if (!isLoadingArts && !isLoadingTaskImages) {
      return mockImages
    }

    return []
  }, [formData.generatedImages, taskImages, isLoadingArts, isLoadingTaskImages])

  const handleApprove = useCallback((imageId) => {
    setSelectedImages((prev) => ({
      ...prev,
      [imageId]: 'approve',
    }))
  }, [])

  const handleReject = useCallback((imageId) => {
    setSelectedImages((prev) => ({
      ...prev,
      [imageId]: 'reject',
    }))
  }, [])

  const allImagesProcessed = useMemo(() => {
    return generatedArts.every((img) => selectedImages[img.id] !== undefined)
  }, [generatedArts, selectedImages])

  const handleProductChange = useCallback((e) => {
    setSelectedProduct(e.target.value)
  }, [])

  const handleValidateAndReplicate = useCallback(async () => {
    if (!selectedProduct) {
      toast({
        title: 'Selecione um produto',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!allImagesProcessed) {
      toast({
        title: 'Avalie todas as artes',
        description: 'É necessário aprovar ou rejeitar todas as artes antes de prosseguir.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsGenerating(true)

    try {
      if (selectedProduct === 'todos') {
        const productsToGenerate = products.filter((p) => p.value !== 'todos')

        toast({
          title: 'Gerando artes para todos os produtos',
          description: 'Este processo pode demorar alguns minutos.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        })

        // Armazenar os IDs de tarefas criadas
        const taskIds = []

        for (const product of productsToGenerate) {
          const aspectRatio = product.aspectRatio

          const requestData = {
            ...formData,
            produto: product.value,
            aspectRatio,
            // Adicionando imagens aprovadas e o taskId original
            approvedImageIds: Object.entries(selectedImages)
              .filter(([_, status]) => status === 'approve')
              .map(([id]) => parseInt(id)),
            sourceTaskId: taskId,
          }

          // Criar tarefa para cada produto
          try {
            const response = await api.post('/api/imagem-ia/gerar', requestData)
            if (response.data && response.data.taskId) {
              taskIds.push(response.data.taskId)
            }
          } catch (error) {
            console.error(`Erro ao gerar arte para ${product.label}:`, error)
          }

          await new Promise((resolve) => setTimeout(resolve, 500))
        }

        toast({
          title: 'Artes geradas para todos os produtos!',
          description: `${taskIds.length} tarefas criadas com sucesso.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        const aspectRatio = getAspectRatio(products, selectedProduct)

        const requestData = {
          ...formData,
          produto: selectedProduct,
          aspectRatio,
          // Adicionando imagens aprovadas e o taskId original
          approvedImageIds: Object.entries(selectedImages)
            .filter(([_, status]) => status === 'approve')
            .map(([id]) => parseInt(id)),
          sourceTaskId: taskId,
        }

        try {
          const response = await api.post('/api/imagem-ia/gerar', requestData)

          if (response.data && response.data.taskId) {
            // Atualizar o formData com o novo taskId
            const updatedFormData = {
              ...formData,
              taskId: response.data.taskId,
            }
            // Atualizar o formData no contexto
            setFormData(updatedFormData)
          }
        } catch (error) {
          console.error('Erro ao gerar arte:', error)
          throw error
        }

        await new Promise((resolve) => setTimeout(resolve, 1000))

        toast({
          title: 'Arte replicada com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      }

      setIsGenerating(false)

      // Log da navegação
      console.log('Navegando para FinalSelectionStep via rota')
      navigate('/pattern/final-selection')
    } catch (error) {
      setIsGenerating(false)
      toast({
        title: 'Erro ao processar artes',
        description: error.message || 'Ocorreu um erro ao processar as artes.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }, [allImagesProcessed, formData, products, selectedImages, selectedProduct, toast, setFormData, taskId, navigate])

  if (isLoadingArts || isLoadingProducts) {
    return (
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" borderColor="#378BBF">
        <Text fontSize="xl" fontWeight="bold" mb={6}>
          Processando sua solicitação
        </Text>

        <Center h="300px" flexDirection="column">
          <Spinner size="xl" color="#378BBF" mb={6} />

          <Box textAlign="center" maxW="500px">
            <Text fontSize="lg" fontWeight="semibold" mb={2}>
              Status:{' '}
              <Text as="span" color="blue.500">
                {taskStatus === 'processing'
                  ? 'Processando imagens'
                  : taskStatus === 'queued'
                    ? 'Na fila de processamento'
                    : taskStatus === 'started'
                      ? 'Iniciando geração'
                      : taskStatus === 'checking'
                        ? 'Verificando status'
                        : taskStatus === 'failed'
                          ? 'Falha no processamento'
                          : taskStatus}
              </Text>
            </Text>

            <Text fontSize="md" color="gray.600" mt={4} mb={6}>
              {taskStatus === 'processing'
                ? 'Estamos gerando suas imagens. Isso pode levar alguns minutos. Por favor, aguarde.'
                : taskStatus === 'queued'
                  ? 'Sua solicitação está na fila e será processada em breve.'
                  : taskStatus === 'started'
                    ? 'Começamos a processar suas imagens. Por favor, aguarde.'
                    : taskStatus === 'checking'
                      ? 'Estamos verificando o status da sua solicitação.'
                      : taskStatus === 'failed'
                        ? 'Houve um problema ao processar suas imagens. Por favor, tente novamente.'
                        : 'Aguardando processamento da sua solicitação.'}
            </Text>

            <Box mt={8}>
              <ActionButton onClick={() => navigate('/pattern')} variant="outline" color="#378BBF" borderColor="#378BBF" size="md" position="static">
                Voltar e criar novo prompt
              </ActionButton>
            </Box>
          </Box>
        </Center>
      </Box>
    )
  }

  const productLabel = products.find((p) => p.value === formData.produto)?.label || 'Produto'

  // Pegamos o prompt da primeira imagem, se disponível
  const promptText = generatedArts[0]?.prompt

  return (
    <Box pb="80px">
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Artes Geradas para {productLabel}
      </Text>

      {promptText && (
        <Text fontSize="md" color="gray.600" mb={4}>
          Prompt utilizado: "{promptText}"
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
        {generatedArts.map((image) => (
          <ArtImage
            key={image.id}
            image={image}
            selectedStatus={selectedImages[image.id]}
            onApprove={handleApprove}
            onReject={handleReject}
            displayType="approval"
            imageHeight="400px"
          />
        ))}
      </SimpleGrid>

      <Box borderTop="1px solid" borderColor="gray.200" pt={6} mb={6}>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Validar e Aplicar em outros produtos
        </Text>

        <Flex direction={{ base: 'column', md: 'row' }} gap={4} mb={4}>
          <Box flex="1">
            <Select
              placeholder="Escolha o produto"
              value={selectedProduct}
              onChange={handleProductChange}
              borderColor="#378BBF"
              _focus={{ borderColor: '#378BBF' }}
              isDisabled={isGenerating}
            >
              {products.map((product) => (
                <option key={product.id} value={product.value}>
                  {product.label}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
      </Box>

      <ActionButton
        onClick={handleValidateAndReplicate}
        isDisabled={!allImagesProcessed || !selectedProduct}
        isLoading={isGenerating}
        loadingText="Processando..."
        position="fixed"
      >
        Validar e Replicar
      </ActionButton>
    </Box>
  )
}
